import React, { useState, useRef, useMemo, useEffect } from 'react';
import styles from './Analytics.module.scss';
import SettingsHeader from 'pages/authenticated/layout/settingsLayout/settingsHeader/SettingsHeader';
import FilterDropdown from 'component/Custom/FilterDropdown/FilterDropdown';
import moment from 'moment';
import Button from 'component/atom/forms/button/Button';
import UserInsights from 'component/organism/analytics/user-insights/UserInsights';
import BotPerformance from 'component/organism/analytics/bot-performance/BotPerformance';
import { useParams } from 'react-router-dom';
import Tab from 'component/molecules/Tab/TabComponent';
import { useUserContext } from 'app-context/user/UserContext';
import {
  Document,
  Page,
  Text,
  Image,
  StyleSheet,
  pdf,
  View,
} from '@react-pdf/renderer';
import { useBrandingContext } from 'app-context/BrandingsContext';
import {
  captureNodeToCanvas,
  CONTENT_WIDTH_PT,
  sliceCanvasToPages,
} from './helpers/capturePdf';
import AnalyticsPdf from './helpers/components/AnalyticsPdf';
import Select from 'component/atom/forms/select/select/Select';
import toast from 'react-hot-toast';
import { SnapshotProvider } from './helpers/components/SnapshotProvider';

const pdfStyles = StyleSheet.create({
  page: { padding: 10, flexDirection: 'column', gap: 8 },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  nameCircle: {
    borderRadius: '50',
    backgroundColor: '#800080',
    height: 24,
    width: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  botInitials: { color: '#fff', fontSize: 12, fontWeight: 700 },
  botName: { fontSize: 12, color: '#011211', fontWeight: 500 },
  title: { fontSize: 16, fontWeight: 700, color: '#011211', marginBottom: 2 },
  subTitle: {
    fontSize: 12,
    fontWeight: 500,
    color: '#4E4E4E',
    marginBottom: 8,
  },
});

const Analytics = () => {
  const { id } = useParams();
  const { selectedBot } = useUserContext();
  const { brandingData } = useBrandingContext();

  const [isExporting, setIsExporting] = useState(false);
  const [loader, setLoader] = useState(false);

  const [days, setDays] = useState('All Data');
  const today = moment().format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(
    moment().subtract(6, 'days').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(today);
  const [botType, setBotType] = useState(selectedBot?.botType === 0 ? 2 : 0);

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const botTypeOptions = useMemo(
    () =>
      selectedBot?.botType === 1
        ? [
            { value: 0, label: 'External Chatbot' },
            { value: 2, label: 'Chat Interface' },
            { value: 1, label: 'Try Me Bot' },
          ]
        : [
            { value: 2, label: 'Chat Interface' },
            { value: 1, label: 'Try Me Bot' },
          ],
    [selectedBot?.botType]
  );
  const exportTargetRef = useRef(null);
  const PAGE_1 = useRef(null);
  const PAGE_2 = useRef(null);
  const PAGE_3 = useRef(null);
  const PAGE_4 = useRef(null);

  const [pendingSnapshots, setPendingSnapshots] = useState(0);

  const [hud, setHud] = useState({
    visible: false,
    stage: 'Preparing…',
    step: 0,
    total: 0,
    elapsedMs: 0,
    etaMs: null,
    progress: 0,
  });
  const t0Ref = useRef(0);
  const tickRef = useRef(null);
  const stepTimesRef = useRef([]);

  function hudStart(total, stage) {
    t0Ref.current = performance.now();
    setHud({
      visible: true,
      stage,
      step: 0,
      total,
      elapsedMs: 0,
      etaMs: null,
      progress: 0,
    });
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setHud((prev) => ({
        ...prev,
        elapsedMs: performance.now() - t0Ref.current,
      }));
    }, 100);
  }
  function hudStepDone(newStage) {
    const avg = stepTimesRef.current.length
      ? stepTimesRef.current.reduce((a, b) => a + b, 0) /
        stepTimesRef.current.length
      : null;
    setHud((prev) => {
      const step = Math.min(prev.total, prev.step + 1);
      const remaining = prev.total - step;
      const etaMs = avg ? remaining * avg + 1200 : null;
      const progress = Math.min(0.95, step / prev.total);
      return { ...prev, step, stage: newStage || prev.stage, etaMs, progress };
    });
  }
  function hudStage(stage) {
    setHud((prev) => ({ ...prev, stage }));
  }
  function hudFinish() {
    setHud((prev) => ({
      ...prev,
      stage: 'Packaging PDF…',
      progress: 1,
      etaMs: 0,
    }));
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    setTimeout(() => setHud((prev) => ({ ...prev, visible: false })), 500);
  }

  const settleFrames = async (frames = 2) => {
    for (let i = 0; i < frames; i++) {
      await new Promise((r) => requestAnimationFrame(() => r()));
    }
  };

  const handleExportPDF = async () => {
    if (isExporting) return;

    if (loader || pendingSnapshots > 0) {
      toast.error('Export blocked: widgets are still loading.');
      return;
    }

    setIsExporting(true);
    document.documentElement.classList.add('__export');
    window.dispatchEvent(new Event('export:start'));

    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch {}
    }

    await settleFrames(2);

    const nodes = [
      { ref: PAGE_1.current, tag: 'user' },
      { ref: PAGE_2.current, tag: 'user' },
      { ref: PAGE_3.current, tag: 'perf' },
      { ref: PAGE_4.current, tag: 'perf' },
    ].filter((n) => n.ref);

    if (!nodes.length) {
      window.dispatchEvent(new Event('export:end'));
      document.documentElement.classList.remove('__export');
      setIsExporting(false);
      return;
    }

    hudStart(nodes.length, 'Packaging PDF…');
    const allPages = [];
    stepTimesRef.current = [];

    for (const { ref, tag } of nodes) {
      const s = performance.now();
      const canvas = await captureNodeToCanvas(ref);
      stepTimesRef.current.push(performance.now() - s);
      hudStepDone('Slicing…');
      const slices = sliceCanvasToPages(canvas);
      slices.forEach((sli) => allPages.push({ ...sli, tag }));
      hudStage('Packaging PDF…');
    }

    window.dispatchEvent(new Event('export:end'));
    hudStage('Packaging PDF…');

    const Doc = () => (
      <Document>
        {allPages.map((p, idx) => {
          const aspect = p.height / p.width;
          return (
            <Page key={idx} size='A4' style={pdfStyles.page}>
              <View style={pdfStyles.headerRow}>
                {selectedBot?.botName ? (
                  <View style={pdfStyles.nameCircle}>
                    <Text style={pdfStyles.botInitials}>
                      {selectedBot.botName
                        .split(' ')
                        .map((w) => w.charAt(0))
                        .join('')}
                    </Text>
                  </View>
                ) : null}
                <Text style={pdfStyles.botName}>
                  {selectedBot?.botName || ''}
                </Text>
              </View>
              <Text style={pdfStyles.title}>Dashboard</Text>
              <Text style={pdfStyles.subTitle}>
                {p.tag === 'user' ? 'User Insights' : 'Bot Performance'}
              </Text>
              <Image
                src={p.src}
                style={{
                  width: CONTENT_WIDTH_PT,
                  height: CONTENT_WIDTH_PT * aspect,
                }}
              />
            </Page>
          );
        })}
      </Document>
    );

    const instance = pdf(<Doc />);
    const blob = await instance.toBlob();
    const url = URL.createObjectURL(blob);

    hudFinish();

    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard-${moment().format('YYYYMMDD-HHmm')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    document.documentElement.classList.remove('__export');
    setIsExporting(false);
  };

  useEffect(() => {
    const ISO_UTC_FMT = 'YYYY-MM-DD[T]HH:mm:ss[Z]';
    const now = moment();
    const startTodayLocal = now.clone().startOf('day');
    const endTodayLocal = now.clone().endOf('day');

    let s;
    let e;

    if (selectedBot?.latestTimestamp) {
      const start = moment(selectedBot.latestTimestamp);
      s = start.clone().utc().format(ISO_UTC_FMT);
      e = now.clone().utc().format(ISO_UTC_FMT);
    } else {
      const fallbackStart = startTodayLocal.clone().subtract(89, 'day');
      s = fallbackStart.clone().utc().format(ISO_UTC_FMT);
      e = endTodayLocal.clone().utc().format(ISO_UTC_FMT);
    }

    setStartDate(s);
    setEndDate(e);
    setDays('All Data');
  }, [id, selectedBot?.latestTimestamp]);

  useEffect(() => {
    if (!botTypeOptions.length) return;

    setBotType((current) => {
      const stillValid =
        current != null && botTypeOptions.some((o) => o.value === current);
      if (stillValid) return current;

      const fromBot = botTypeOptions.find(
        (o) => o.value === selectedBot?.botType
      )?.value;

      return fromBot ?? botTypeOptions[0].value;
    });
  }, [selectedBot?.botType, botTypeOptions, id]);
  const rightItem = (
    <div className={styles['right-item']}>
      <Select
        placeholder='Type'
        selectClassname={styles['select']}
        classname={styles['select-container']}
        inputClassName={styles['select-input']}
        value={botType}
        onChange={(e) => setBotType(e.target.value)}
        options={botTypeOptions}
      />
      <FilterDropdown
        days={days}
        handleSetDays={setDays}
        handleOpenCustomDates={() => {}}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        variant='secondary'
        allDataStart={selectedBot?.latestTimestamp}
      />
      <Button
        label={
          isExporting
            ? 'Exporting…'
            : pendingSnapshots > 0
            ? `Waiting (${pendingSnapshots})…`
            : 'Export to PDF'
        }
        onClick={handleExportPDF}
        disabled={isExporting || loader || pendingSnapshots > 0}
        className={styles['export-btn']}
      />
    </div>
  );

  const tabs = useMemo(
    () => [
      {
        label: 'User Insights',
        content: (
          <UserInsights
            botId={id}
            startDate={startDate}
            endDate={endDate}
            days={days}
            isExporting={isExporting}
            botType={botType}
          />
        ),
      },
      {
        label: 'Bot Performance',
        content: (
          <BotPerformance
            botId={id}
            startDate={startDate}
            endDate={endDate}
            botType={botType}
          />
        ),
      },
    ],
    [id, startDate, endDate, days, botType, isExporting]
  );

  return (
    <>
      <div
        ref={exportTargetRef}
        style={{
          position: 'fixed',
          background: '#fff',
          left: -99999,
          top: 0,
          opacity: 0,
          pointerEvents: 'none',
        }}
      >
        <SnapshotProvider onChange={setPendingSnapshots}>
          <AnalyticsPdf
            page1Ref={PAGE_1}
            page2Ref={PAGE_2}
            page3Ref={PAGE_3}
            page4Ref={PAGE_4}
            id={id}
            startDate={startDate}
            endDate={endDate}
            days={days}
            brandingData={brandingData}
            setLoader={setLoader}
            styles={styles}
            botType={botType}
          />
        </SnapshotProvider>
      </div>

      <SettingsHeader title='Analytics' rightItem={rightItem} />
      <Tab tabs={tabs} onTabChange={(i) => setActiveTabIndex(i)} />

      {hud.visible && (
        <div className={styles['export-overlay']}>
          <div className={styles['export-card']}>
            <h3 className={styles['export-title']}>{hud.stage}</h3>
            <div className={styles['export-progress']}>
              <div
                className={styles['export-bar']}
                style={{ width: `${Math.round(hud.progress * 100)}%` }}
              />
            </div>
            <div className={styles['export-meta']}>
              <span>
                {Math.min(hud.step, hud.total)}/{hud.total} pages
              </span>
              <span>{Math.round(hud.progress * 100)}%</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Analytics;
